import { alpha, Box, Button, CircularProgress, Divider, TextField, Typography } from "@mui/material";
import React, { FC, useEffect } from "react";
import { Author, SubmitButtonTextEnum, SubmitButtonEnum, SubmitButtonType, TabKeyEnum } from "../../utils/types";
import { useCreateAuthorMutation, useEditAuthorMutation } from "../../api/apiSlice";
import toast from "react-hot-toast";
import { useDetectChanges, useDisableButtons } from "../../hooks";

interface CreateOrEditAuthorProps {
  activeTab: TabKeyEnum;
  author?: Author | null;
  handleClose: () => void;
  isEditUser?: boolean;
}
export const CreateOrEditAuthor: FC<CreateOrEditAuthorProps> = props => {
  const { author, handleClose, isEditUser, activeTab } = props;

  const [isEditing, setIsEditing] = React.useState<boolean>(isEditUser || false);

  const [authorDetails, setAuthorDetails] = React.useState<Partial<Author> | null>(author || null);
  const [clickedButton, setClickedButton] = React.useState<SubmitButtonType | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAuthorDetails({ ...authorDetails, name: event.target.value });
    if (event.target.value) {
      setError(null);
      return;
    }
    setError("Author is required");
  };

  useEffect(() => {
    setIsEditing(isEditUser || false);
  }, [isEditUser]);

  const [createUser, createUserResult] = useCreateAuthorMutation();
  const [editUser, editUserResult] = useEditAuthorMutation();
  const { isLoading: isLoadingAddUser, isSuccess } = createUserResult;
  const { isSuccess: isEditSuccess, isLoading: isEditLoading } = editUserResult;

  const isLoading = isLoadingAddUser || isEditLoading;

  const handleSubmit = (published: -1 | 1 | 0, buttonType: SubmitButtonType) => {
    setClickedButton(buttonType);
    if (authorDetails?.name) {
      const name = authorDetails.name.trim();
      if (isEditing && authorDetails?.id) {
        editUser({
          id: authorDetails.id,
          name,
          published,
        })
          .unwrap()
          .then(() => {
            toast.success("Changes saved successfully");
          })
          .catch(error => {
            toast.error(error?.data || "Failed to save changes");
          });
      } else {
        createUser({
          name,
          published,
        })
          .unwrap()
          .then(() => {
            toast.success("Author added successfully");
          })
          .catch(error => {
            toast.error(error?.data || "Failed to create author");
          });
      }
    }
  };

  useEffect(() => {
    if (isSuccess || isEditSuccess) {
      handleClose();
    }
  }, [handleClose, isEditSuccess, isSuccess]);

  // Only check for changes on the name field
  const hasChanges = useDetectChanges<Partial<Author>>({ initialObject: { name: author?.name }, object: { name: authorDetails?.name } });
  const hasRequiredFields = !!authorDetails?.name?.trim();
  const { disableReadyForReviewButton, disableSaveAsUnpublishedButton } = useDisableButtons({
    isLoading,
    hasRequiredFields,
    isEditing,
    hasChanges: hasChanges(),
    activeTab,
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      sx={{
        width: "100%",
      }}
    >
      <TextField
        label="Author"
        value={authorDetails?.name || ""}
        onChange={handleOnChange}
        onBlur={handleOnChange}
        fullWidth
        required
        error={!!error}
        helperText={error}
        margin="normal"
      />
      <Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" sx={{ py: 3, gap: 2 }}>
          <Button
            onClick={() => handleSubmit(-1, SubmitButtonEnum.ReadyForReview)}
            disabled={disableReadyForReviewButton}
            sx={{ borderRadius: "20px" }}
            disableElevation
            color="primary"
            variant="contained"
            type="button"
          >
            {isLoading && clickedButton === SubmitButtonEnum.ReadyForReview ? <CircularProgress size={16} /> : ""}
            {SubmitButtonTextEnum.ReadyForReview}
          </Button>
          <Button
            onClick={() => handleSubmit(0, SubmitButtonEnum.SaveAsUnpublished)}
            disabled={disableSaveAsUnpublishedButton}
            sx={theme => ({ flex: 1, borderRadius: "20px", backgroundColor: alpha(theme.palette.primary.main, 0.5) })}
            disableElevation
            color="primary"
            variant="contained"
            type="button"
          >
            {isLoading && clickedButton === SubmitButtonEnum.SaveAsUnpublished ? <CircularProgress size={16} /> : ""}
            {SubmitButtonTextEnum.SaveAsUnpublished}
          </Button>
          <Button disableElevation variant="outlined" sx={{ borderRadius: "20px" }} onClick={handleClose} type="button">
            <Typography sx={{ fontSize: "16px" }} color="textPrimary">
              Cancel
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
